// components/MessageBubble.jsx
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { UserIcon, CpuChipIcon } from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import Plot from 'react-plotly.js';

// Tailwind-styled renderers so markdown (bold, lists, headings) displays correctly.
// Tailwind's preflight strips default list/heading styles, so we re-add them here.
const markdownComponents = {
  p: (props) => <p className="mb-2 last:mb-0 break-words" {...props} />,
  strong: (props) => <strong className="font-semibold" {...props} />,
  em: (props) => <em className="italic" {...props} />,
  ul: (props) => <ul className="list-disc pl-5 mb-2 space-y-1" {...props} />,
  ol: (props) => <ol className="list-decimal pl-5 mb-2 space-y-1" {...props} />,
  li: (props) => <li className="break-words" {...props} />,
  h1: (props) => <h3 className="font-semibold mt-2 mb-1" {...props} />,
  h2: (props) => <h3 className="font-semibold mt-2 mb-1" {...props} />,
  h3: (props) => <h3 className="font-semibold mt-2 mb-1" {...props} />,
  blockquote: (props) => (
    <blockquote className="border-l-2 border-gray-300 pl-3 italic text-gray-600 my-2" {...props} />
  ),
  a: (props) => <a className="text-blue-600 underline" target="_blank" rel="noopener noreferrer" {...props} />,
  code: (props) => <code className="bg-gray-200 rounded px-1" {...props} />,
};

const MessageBubble = ({ message, isUser, isTyping = false }) => {
  const [displayText, setDisplayText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [showDotLoader, setShowDotLoader] = useState(true);
  
  // Reset when message changes
  useEffect(() => {
    if (!isUser && message.message) {
      setDisplayText('');
      setCurrentIndex(0);
      setIsComplete(false);
      setShowDotLoader(true);
    }
  }, [message.message, isUser]);
  
  // Typewriter effect
  useEffect(() => {
    // Only apply typewriter for assistant messages that are still typing
    if (!isUser && isTyping && !isComplete && message.message && message.message.length > 0) {
      // Hide dot loader when first character appears
      if (showDotLoader && currentIndex === 0) {
        setShowDotLoader(false);
      }
      
      if (currentIndex < message.message.length) {
        const timeout = setTimeout(() => {
          setDisplayText(prev => prev + message.message[currentIndex]);
          setCurrentIndex(prev => prev + 1);
          
          // Auto-scroll while typing
          const messagesContainer = document.querySelector('.overflow-y-auto');
          if (messagesContainer) {
            messagesContainer.scrollTop = messagesContainer.scrollHeight;
          }
        }, 25); // Speed: 25ms per character
        
        return () => clearTimeout(timeout);
      } else if (currentIndex >= message.message.length && !isComplete) {
        setIsComplete(true);
        setShowDotLoader(false);
      }
    } else if (!isUser && !isTyping && !isComplete && message.message) {
      // If not typing mode, show full text immediately
      setDisplayText(message.message);
      setIsComplete(true);
      setShowDotLoader(false);
    } else if (isUser) {
      // User messages show immediately
      setDisplayText(message.message);
      setShowDotLoader(false);
    }
  }, [currentIndex, message.message, isUser, isTyping, isComplete, showDotLoader]);
  
  // Show dot loader while waiting for response
  if (!isUser && isTyping && (!message.message || message.message.length === 0) && showDotLoader) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-start space-x-3"
      >
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-gradient-to-br from-gray-500 to-gray-600 rounded-full flex items-center justify-center">
            <CpuChipIcon className="h-4 w-4 text-white" />
          </div>
        </div>
        <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-5 py-3">
          <div className="flex space-x-1.5 items-center">
            <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
            <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
          </div>
        </div>
      </motion.div>
    );
  }
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`flex items-start space-x-3 ${isUser ? 'flex-row-reverse space-x-reverse' : ''}`}
    >
      <div className="flex-shrink-0">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
          isUser 
            ? 'bg-gradient-to-br from-blue-500 to-blue-600' 
            : 'bg-gradient-to-br from-gray-500 to-gray-600'
        }`}>
          {isUser ? (
            <UserIcon className="h-4 w-4 text-white" />
          ) : (
            <CpuChipIcon className="h-4 w-4 text-white" />
          )}
        </div>
      </div>
      
      <div className={`${!isUser && message.shapPlot ? 'max-w-[90%] w-full' : 'max-w-[70%]'} ${isUser ? 'items-end' : 'items-start'}`}>
        <div className={`rounded-2xl px-4 py-2 ${
          isUser 
            ? 'bg-blue-600 text-white rounded-tr-sm' 
            : 'bg-gray-100 text-gray-800 rounded-tl-sm'
        }`}>
          {isUser ? (
            <p className="text-sm whitespace-pre-wrap break-words">
              {displayText || message.message}
            </p>
          ) : (
            <div className="text-sm break-words leading-relaxed">
              <ReactMarkdown components={markdownComponents}>
                {displayText || message.message}
              </ReactMarkdown>
              {/* Blinking cursor for active typing */}
              {isTyping && !isComplete && displayText && displayText.length < message.message?.length && (
                <span className="inline-block w-0.5 h-4 bg-gray-600 ml-0.5 animate-pulse">|</span>
              )}
            </div>
          )}
        </div>

        {/* Inline SHAP plot — rendered when the assistant returned one */}
        {!isUser && message.shapPlot && message.shapPlot.data && (
          <div className="mt-2 bg-white rounded-xl border border-gray-200 p-3">
            <Plot
              data={message.shapPlot.data}
              layout={{
                ...message.shapPlot.layout,
                autosize: true,
                margin: { l: 150, r: 30, t: 40, b: 40 },
              }}
              config={{ responsive: true, displaylogo: false }}
              style={{ width: '100%', height: '360px' }}
              useResizeHandler={true}
            />
          </div>
        )}
        <div className={`text-xs text-gray-400 mt-1 ${isUser ? 'text-right' : 'text-left'}`}>
          {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      </div>
    </motion.div>
  );
};

export default MessageBubble;